import {
  FC,
  MouseEvent,
  ReactNode,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SettingsIcon from '@mui/icons-material/Settings';
import TranslateIcon from '@mui/icons-material/Translate';
import RenterNotificationIcon, {
  RenterNotificationIconHandle,
} from 'src/components/NewRenterInterface/RenterNotificationIcon';
import useAuth from 'src/hooks/useAuth';
import { NavItem } from 'src/types/NavBarTypes';
import { RootState } from 'src/redux/rootReducerType';
import { toggleExpanded } from 'src/redux/slices/NewRenterInterface/NewRentersInterfaceUIManagementSlice';
import useSettings from 'src/hooks/useSettings';
import useGoogleTranslate, { LANGUAGES } from 'src/hooks/useGoogleTranslate';
import { NAV_WIDTH_EXPANDED, NAV_WIDTH_COLLAPSED } from './NavBarConstants';
import DesktopNavBarButton from 'src/components/NewRenterInterface/NavBar/DesktopNavBarButton';
import ProntoLogo from 'src/components/ProntoLogo';
import {
  getUserDisplayName,
  getUserInitial,
} from 'src/components/NewRenterInterface/utils/NewRentersInterfaceUtils';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import {
  selectElevatedAuthIsValid,
  setElevatedAuth,
  selectHasPendingVerifications,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import elevatedAuthService from 'src/services/elevatedAuth.service';
import VerifyCodeModal from 'src/components/NewRenterInterface/AccountHub/VerifyCodeModal';
import PendingVerificationsModal from 'src/components/Ulcm/PendingVerificationsModal';

interface RenterNavBarDesktopProps {
  navItems: NavItem[];
  onNavigate: (path: string, external?: boolean) => void;
  isItemActive: (item: NavItem) => boolean;
}

const RenterNavBarDesktop: FC<RenterNavBarDesktopProps> = ({
  navItems,
  onNavigate,
  isItemActive,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );
  const expanded = useSelector(
    (state: RootState) => state.newRenterUI.expanded
  );
  const drawerWidth = expanded ? NAV_WIDTH_EXPANDED : NAV_WIDTH_COLLAPSED;
  const { themeMode, toggleThemeMode } = useSettings();
  const { currentLang, currentLanguageLabel, selectLanguage } =
    useGoogleTranslate();

  const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [showPendingVerifications, setShowPendingVerifications] =
    useState(false);
  const notificationRef = useRef<RenterNotificationIconHandle>(null);

  const isElevatedAuthValid = useSelector(selectElevatedAuthIsValid);
  const hasPendingVerifications = useSelector(selectHasPendingVerifications);
  const totpEnabled = useSelector(
    (state: RootState) =>
      state.newRenterInterfaceUser.accountHub.elevatedAuth.totp_enabled
  );
  const userEmail = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user?.email
  );
  const userEmailVerified = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user?.email_verified
  );
  const userPhoneVerified = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user?.phone_verified
  );
  const hasRealEmail = !!userEmail && !userEmail.endsWith('@prontohousing.io');
  const otpFirstChannel: 'email' | 'sms' = hasRealEmail ? 'email' : 'sms';
  // Only treat the user as having no verified contact when there are pending
  // verifications AND they have neither a verified real email nor a verified
  // phone — i.e. they truly cannot receive an OTP through any channel.
  const hasUsableContact =
    (hasRealEmail && userEmailVerified === true) || userPhoneVerified === true;
  const hasNoVerifiedContact = hasPendingVerifications && !hasUsableContact;

  const handleToggleNotifications = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      notificationRef.current?.handleClick(event);
    },
    []
  );

  const handleToggleExpanded = (): void => {
    dispatch(toggleExpanded());
  };

  const handleThemeChange = useCallback(
    (_: MouseEvent<HTMLElement>, newMode: string | null) => {
      if (newMode !== null && newMode !== themeMode) {
        toggleThemeMode();
      }
    },
    [themeMode, toggleThemeMode]
  );

  const handleOpenSettings = useCallback((event: MouseEvent<HTMLElement>) => {
    setSettingsAnchorEl(event.currentTarget);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setSettingsAnchorEl(null);
  }, []);

  const handleLogout = useCallback(async () => {
    handleCloseSettings();
    await logout();
  }, [logout, handleCloseSettings]);

  const handleAccountHubClick = useCallback(async () => {
    handleCloseSettings();
    if (isElevatedAuthValid) {
      navigate(`/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}`);
      return;
    }
    if (hasNoVerifiedContact) {
      setShowPendingVerifications(true);
      return;
    }
    try {
      const res = await elevatedAuthService.getStatus();
      dispatch(
        setElevatedAuth({
          verified: res.verified,
          expires_at: res.expires_at ?? undefined,
          totp_enabled: res.totp_enabled,
        })
      );
    } catch {
      // Non-fatal — modal will use whatever is in Redux already
    }
    setVerifyOpen(true);
  }, [
    dispatch,
    handleCloseSettings,
    hasNoVerifiedContact,
    isElevatedAuthValid,
    navigate,
  ]);

  const handleVerified = useCallback(
    (expiresAt: string, method: 'otp' | 'totp'): void => {
      dispatch(
        setElevatedAuth({
          verified: true,
          expires_at: expiresAt,
          verified_at: new Date().toISOString(),
          method,
        })
      );
      setVerifyOpen(false);
      navigate(`/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}`);
    },
    [dispatch, navigate]
  );

  const handleVerifyCancel = useCallback(() => {
    setVerifyOpen(false);
  }, []);

  const userName = getUserDisplayName(user);
  const userInitial = getUserInitial(user);

  const LOWER_SECTION_BUTTONS: NavItem[] = [
    {
      label: 'Settings',
      icon: <SettingsIcon sx={{ fontSize: 18 }} />,
      onClick: handleOpenSettings,
    },
  ];

  const getButtonForNavigationItem = (item: NavItem): ReactNode => (
    <DesktopNavBarButton
      key={item.label}
      item={item}
      isActive={isItemActive(item)}
      onClick={() => onNavigate(item.path, item.external)}
    />
  );

  const getButtonForItem = (item: NavItem): ReactNode => (
    <DesktopNavBarButton
      key={item.label}
      item={item}
      isActive={false}
      onClick={item.onClick}
    />
  );

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'primary.dark',
            color: 'common.white',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: expanded
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
            }),
          },
        }}
      >
        <Box
          sx={{
            p: 2,
          }}
        >
          <ProntoLogo expanded={expanded} height={28} fill="currentColor" />
        </Box>
        <Divider />
        <List disablePadding sx={{ mt: 2 }}>
          {getButtonForItem({
            label: 'Notifications',
            icon: (
              <RenterNotificationIcon
                iconSize={18}
                ref={notificationRef}
                iconColor="common.white"
              />
            ),
            onClick: handleToggleNotifications,
          })}
        </List>
        <Divider />
        <List disablePadding sx={{ mt: 2 }}>
          {navItems.map((item) => getButtonForNavigationItem(item))}
        </List>

        <Box sx={{ mt: 'auto', mb: 2 }}>
          <Divider sx={{ mb: 1 }} />
          <List component="ul" disablePadding>
            {LOWER_SECTION_BUTTONS.map((item) => getButtonForItem(item))}
          </List>
        </Box>

        <Menu
          anchorEl={settingsAnchorEl}
          open={Boolean(settingsAnchorEl)}
          onClose={handleCloseSettings}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{ paper: { sx: { minWidth: 200 } } }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: 'primary.main',
                  userSelect: 'none',
                }}
              >
                {userInitial}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                textTransform="capitalize"
              >
                {userName}
              </Typography>
            </Box>
          </Box>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </MenuItem>
          <MenuItem onClick={handleAccountHubClick}>
            <ListItemIcon>
              <ManageAccountsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Account Hub" />
          </MenuItem>

          <Divider />

          <ListItem>
            <ListItemIcon>
              <TranslateIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Language"
              secondary={currentLanguageLabel}
              slotProps={{ secondary: { variant: 'caption' } }}
            />
          </ListItem>
          <Box sx={{ px: 2, pb: 2 }}>
            <Box
              sx={{
                maxHeight: 200,
                overflowY: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              {LANGUAGES.map((lang) => (
                <ListItemButton
                  key={lang.code}
                  className="notranslate"
                  onClick={() => {
                    handleCloseSettings();
                    selectLanguage(lang.code);
                  }}
                  selected={currentLang === lang.code}
                  sx={{ py: 1 }}
                >
                  <ListItemText
                    primary={lang.label}
                    slotProps={{ primary: { variant: 'body2' } }}
                  />
                  {currentLang === lang.code && (
                    <CheckIcon fontSize="small" color="primary" />
                  )}
                </ListItemButton>
              ))}
            </Box>
          </Box>

          <Divider />

          <ListItem>
            <ListItemIcon>
              {themeMode === 'dark' ? (
                <DarkModeIcon fontSize="small" />
              ) : (
                <LightModeIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText primary="Theme" />
          </ListItem>
          <Box sx={{ px: 2, pb: 2 }}>
            <ToggleButtonGroup
              value={themeMode}
              exclusive
              onChange={handleThemeChange}
              fullWidth
              size="small"
            >
              <ToggleButton value="light">
                <LightModeIcon sx={{ mr: 1 }} fontSize="small" />
                Light
              </ToggleButton>
              <ToggleButton value="dark">
                <DarkModeIcon sx={{ mr: 1 }} fontSize="small" />
                Dark
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Menu>

        <IconButton
          onClick={handleToggleExpanded}
          size="small"
          aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          sx={{
            position: 'absolute',
            left: drawerWidth - 12,
            top: '5%',
            transform: 'translateY(-50%)',
            backgroundColor: 'primary.dark',
            border: 1,
            borderColor: 'rgba(255,255,255,0.2)',
            color: 'common.white',
            width: 24,
            height: 24,
            transition: theme.transitions.create('left', {
              easing: theme.transitions.easing.sharp,
              duration: expanded
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
            }),
            '&:hover': {
              backgroundColor: (t) => alpha(t.palette.common.white, 0.15),
            },
          }}
        >
          <ChevronRightIcon
            sx={{
              fontSize: 16,
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: theme.transitions.create('transform', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.short,
              }),
            }}
          />
        </IconButton>
      </Drawer>

      {verifyOpen && !showPendingVerifications && (
        <VerifyCodeModal
          purpose="elevated_auth"
          totpEnabled={totpEnabled}
          preferredChannel={otpFirstChannel}
          dismissable
          onSuccess={handleVerified}
          onCancel={handleVerifyCancel}
          onUnverifiedContact={() => {
            setVerifyOpen(false);
            setShowPendingVerifications(true);
          }}
        />
      )}
      {showPendingVerifications && (
        <PendingVerificationsModal
          onDismiss={() => setShowPendingVerifications(false)}
        />
      )}
    </>
  );
};

export default RenterNavBarDesktop;
